import { useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Checkbox,
  Group,
  Text,
  Grid,
  Select,
} from "@mantine/core";
import { PerformRequest } from "../../../hooks/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import {
  HttpCodes,
  PermissionLabels,
  AdminPermissionList,
} from "../../../lib/data";
import { useAdminContext } from "../../../context/AdminContext";

interface InviteAdminModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const InviteAdminModal = ({
  opened,
  onClose,
  onSuccess,
}: InviteAdminModalProps) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<string>("admin");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { admin: currentUser } = useAdminContext();

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);

    if (!email) {
      setError("Email is required");
      setLoading(false);
      return;
    }

    const endpoint = Endpoints.InviteAdmin();
    const response = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
      data: {
        email,
        firstName,
        lastName,
        permissions: selectedPermissions,
        role,
      },
    });

    setLoading(false);

    if (
      response.status === HttpCodes.Success ||
      response.status === HttpCodes.Created
    ) {
      onSuccess();
      onClose();
      // Reset form
      setEmail("");
      setFirstName("");
      setLastName("");
      setSelectedPermissions([]);
    } else {
      setError(response.message || "Failed to invite admin");
    }
  };

  const togglePermission = (perm: string) => {
    if (selectedPermissions.includes(perm)) {
      setSelectedPermissions((prev) => prev.filter((p) => p !== perm));
    } else {
      setSelectedPermissions((prev) => [...prev, perm]);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Invite New Admin"
      centered
      size="lg"
    >
      <Stack gap="md">
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid.Col>
        </Grid>

        <TextInput
          label="Email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Select
          label="Role"
          placeholder="Select role"
          data={[
            { value: "admin", label: "Admin" },
            ...(currentUser?.role === "super_admin"
              ? [{ value: "super_admin", label: "Super Admin" }]
              : []),
          ]}
          value={role}
          onChange={(value) => setRole(value || "admin")}
          allowDeselect={false}
        />

        {role === "admin" && (
          <>
            <Text size="sm" fw={500} mt="md">
              Permissions
            </Text>
            <Grid>
              {AdminPermissionList.map((perm) => (
                <Grid.Col span={6} key={perm}>
                  <Checkbox
                    label={PermissionLabels[perm]}
                    checked={selectedPermissions.includes(perm)}
                    onChange={() => togglePermission(perm)}
                  />
                </Grid.Col>
              ))}
            </Grid>
          </>
        )}

        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            Send Invite
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
